import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { displayFor, hideSurveyAnswer, hideSurveyWord, setSurveyLive, surveyLink, type ItemDisplay } from '../lib/surveys'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { SURVEYS } from '../lib/hubCopy'
import { SurveyItemResult } from '../components/SurveyResults'
import { BroadcastIcon, ChevronIcon, CloseIcon, CloudIcon, ExpandIcon, ListIcon, PeopleIcon, PillsIcon, QrIcon } from '../components/icons'
import logo from '../assets/conetec-logo.svg'
import { useLiveResults } from './Surveys'

/** How long the mouse can rest before the dock and the cursor step aside. */
const IDLE_MS = 2500

/**
 * `#/surveys/:id/present` — the screen on the wall (310; restyled 315, David
 * 25 Oct: "nice controls, logo").
 *
 * A stage, not a page: the Conetec logo and the survey across the top with the
 * response count; a navy join panel with the QR code on the left; the question
 * and its answers filling the rest, refreshed every few seconds. The presenter's
 * controls live in a dock along the bottom that fades out, with the cursor,
 * when the mouse rests - so the room sees the answers, not the buttons - and
 * comes back on any movement or key.
 *
 * Keys (a clicker sends PageUp / PageDown): ← → move, Q the QR code, F full
 * screen. Names are OFF here by default even on a survey that records them:
 * this is a room, not a report.
 *
 * Read through survey_live_results(), which refuses anybody who is not an
 * owner or viewer (or asked, when results are shared).
 */
export function SurveyPresent() {
  const { id = '' } = useParams()
  const t = SURVEYS[useHubLang()]
  const { results: r, error, refresh } = useLiveResults(id, 3000)
  const [at, setAt] = useState(0)
  const [display, setDisplay] = useState<Record<string, ItemDisplay>>({})
  const [names, setNames] = useState(false)
  const [showQr, setShowQr] = useState(true)
  const [qr, setQr] = useState<string | null>(null)
  const [full, setFull] = useState(false)
  const link = surveyLink(window.location.origin, id)
  // 312. Run live: the question on this screen is the only one phones can
  // answer. The server holds which one (live_item_id); this screen moves it.
  const [liveError, setLiveError] = useState<string | null>(null)
  const localMove = useRef(0)
  const synced = useRef(false)

  // The dock rests out of sight while the mouse does; it never hides while the
  // pointer is on it or something in it has keyboard focus.
  const [idle, setIdle] = useState(false)
  const idleTimer = useRef<number | undefined>(undefined)
  const dockRef = useRef<HTMLElement | null>(null)
  const wake = useCallback(() => {
    setIdle(false)
    window.clearTimeout(idleTimer.current)
    idleTimer.current = window.setTimeout(() => {
      const dock = dockRef.current
      if (dock && (dock.matches(':hover') || dock.contains(document.activeElement))) return
      setIdle(true)
    }, IDLE_MS)
  }, [])
  useEffect(() => {
    wake()
    return () => window.clearTimeout(idleTimer.current)
  }, [wake])

  useEffect(() => {
    const onFull = () => setFull(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFull)
    return () => document.removeEventListener('fullscreenchange', onFull)
  }, [])
  const toggleFull = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())

  useEffect(() => {
    let live = true
    import('qrcode')
      .then((m) => ((m as unknown as { default?: typeof m }).default ?? m))
      .then((qrcode) => qrcode.toDataURL(link, { margin: 1, width: 640, errorCorrectionLevel: 'M', color: { dark: '#002b57', light: '#ffffff' } }))
      .then((url: string) => live && setQr(url))
      .catch(() => live && setQr(null))
    return () => {
      live = false
    }
  }, [link])

  const count = r?.items.length ?? 0
  const live = !!r?.liveItemId
  const owner = r?.myAccess === 'owner'

  function goTo(i: number) {
    if (!r || count === 0) return
    const next = Math.max(0, Math.min(i, count - 1))
    setAt(next)
    if (live && owner && r.items[next].itemId !== r.liveItemId) {
      localMove.current = Date.now()
      setSurveyLive(id, r.items[next].itemId).then(refresh).catch((e) => setLiveError(messageOf(e)))
    }
  }

  function toggleLive() {
    if (!r || count === 0) return
    localMove.current = Date.now()
    setLiveError(null)
    setSurveyLive(id, live ? null : r.items[Math.min(at, count - 1)].itemId)
      .then(refresh)
      .catch((e) => setLiveError(messageOf(e)))
  }

  // Land on the live question when the screen opens, and follow it if another
  // screen (a second owner, a reload) moves it - but not in the moments after
  // this screen moved it itself, or a slow poll would pull it back.
  useEffect(() => {
    if (!r?.liveItemId) {
      synced.current = true
      return
    }
    if (synced.current && Date.now() - localMove.current < 5000) return
    const i = r.items.findIndex((x) => x.itemId === r.liveItemId)
    if (i >= 0 && (i !== at || !synced.current)) setAt(i)
    synced.current = true
  }, [r?.liveItemId])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Only TYPING swallows the keys. A checkbox or a button keeps focus after a
      // click (the Names box did, 25 Oct), and the arrows must still move on.
      const el = e.target as HTMLElement | null
      const typing = el instanceof HTMLTextAreaElement || el?.isContentEditable ||
        (el instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit'].includes(el.type))
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return
      wake()
      if (e.key === 'ArrowRight' || e.key === 'PageDown') goTo(at + 1)
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') goTo(at - 1)
      if (e.key.toLowerCase() === 'q') setShowQr((v) => !v)
      if (e.key.toLowerCase() === 'f') toggleFull()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  if (error && !r) {
    return (
      <div className="stage stage--message">
        <p role="alert">{error}</p>
        <p><Link to="/surveys">{t.back}</Link></p>
      </div>
    )
  }
  if (!r) return <div className="stage stage--message">{t.loading}</div>

  const item = r.items[Math.min(at, Math.max(count - 1, 0))]
  const textual = item && (item.kind === 'words' || item.kind === 'text')
  const ideas = item?.kind === 'ideas'
  const shown: ItemDisplay | undefined = item ? display[item.itemId] : undefined
  const current = item ? (shown && shown !== 'auto' ? shown : displayFor(item.kind, item.display)) : 'list'
  const asCloud = current === 'cloud'
  const asPills = current === 'pills'
  const shortLink = link.replace(/^https?:\/\//, '')

  return (
    <div
      className={`stage${showQr ? '' : ' stage--noqr'}${idle ? ' stage--idle' : ''}`}
      onMouseMove={wake}
      onPointerDown={wake}
    >
      <header className="stage__top">
        <img className="stage__logo" src={logo} alt="Conetec" />
        <h1 className="stage__title">{r.title}</h1>
        <div className="stage__stats" aria-live="polite">
          {live && (
            <span className="stage__live">
              <span className="surveylive__dot" aria-hidden="true" /> {t.liveTag}
            </span>
          )}
          {/* 317 (David, 27 Oct: "the responses count doesn't reset for a new
              question"): the count is THIS question's - ideas and the people
              who gave them, or who answered out of everyone who took part. */}
          <span className="stage__count">
            {item && ideas && item.answered !== null ? (
              <>
                <span className="stage__countn">{item.answered}</span>
                <span className="stage__countl">
                  {t.ideasCount(item.answered).replace(/^\d+\s*/, '')}
                  <span className="stage__counto">{t.peopleCount(item.people ?? 0)}</span>
                </span>
              </>
            ) : item && item.people !== null ? (
              <>
                <span className="stage__countn">{item.people}</span>
                <span className="stage__countl">
                  {t.answeredThis}
                  <span className="stage__counto">{t.ofPeople(r.submissions)}</span>
                </span>
              </>
            ) : (
              <>
                <span className="stage__countn">{r.submissions}</span>
                <span className="stage__countl">{t.responses(r.submissions).replace(/^\d+\s*/, '')}</span>
              </>
            )}
          </span>
        </div>
      </header>

      <div className="stage__body">
        {showQr && (
          <aside className="stage__join">
            <p className="stage__scan">{t.scanToAnswer}</p>
            <div className="stage__qrcard">
              {qr ? <img src={qr} alt="QR code that opens this survey" /> : <div className="stage__qrwait" />}
            </div>
            <p className="stage__hint">{t.scanHint}</p>
            <p className="stage__url">{shortLink}</p>
            {r.status !== 'open' && <p className="stage__state">{r.status === 'closed' ? t.closedNote : t.draftNote}</p>}
          </aside>
        )}

        <main className="stage__main">
          {!item ? (
            <p className="stage__empty">{t.noAnswers}</p>
          ) : (
            <>
              <p className="stage__eyebrow">{t.questionOf(at + 1, count)}</p>
              <h2 className="stage__prompt" key={item.itemId}>{item.prompt}</h2>
              {liveError && <p className="stage__state" role="alert">{liveError}</p>}
              <div className="stage__result" key={`r-${item.itemId}`}>
                {r.suppressed ? (
                  <p className="stage__empty">{t.waitingFor(r.minToShow)}</p>
                ) : (
                  <SurveyItemResult
                    item={item}
                    display={shown}
                    big
                    showNames={names}
                    owner={owner}
                    onHideWord={owner ? (w, h) => hideSurveyWord(item.itemId, w, h).then(refresh) : undefined}
                    onHideAnswer={owner ? (a, h) => hideSurveyAnswer(a, h).then(refresh) : undefined}
                  />
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {count > 1 && (
        <div className="stage__progress" aria-hidden="true">
          <span style={{ inlineSize: `${((at + 1) / count) * 100}%` }} />
        </div>
      )}

      <nav
        className="dock"
        aria-label="Presenter controls"
        ref={dockRef}
        onFocus={wake}
      >
        <div className="dock__group" role="group" aria-label="Questions">
          <button type="button" className="dock__btn dock__btn--icon" aria-label="Previous question" title="Previous (←)" disabled={at === 0} onClick={() => goTo(at - 1)}>
            <span className="dock__flip"><ChevronIcon size={20} /></span>
          </button>
          {r.items.map((it, i) => (
            <button
              key={it.itemId}
              type="button"
              className={`dock__step${i === at ? ' dock__step--on' : ''}${it.itemId === r.liveItemId ? ' dock__step--live' : ''}`}
              aria-current={i === at ? 'step' : undefined}
              title={it.prompt}
              onClick={() => goTo(i)}
            >
              {i + 1}
            </button>
          ))}
          <button type="button" className="dock__btn dock__btn--icon" aria-label="Next question" title="Next (→)" disabled={at >= count - 1} onClick={() => goTo(at + 1)}>
            <ChevronIcon size={20} />
          </button>
        </div>

        {(textual || ideas || (r.identified && r.myAccess)) && (
          <div className="dock__group" role="group" aria-label="Show">
            {ideas && (
              <>
                <button type="button" className={`dock__btn${asPills ? ' dock__btn--on' : ''}`} aria-pressed={asPills} onClick={() => setDisplay((x) => ({ ...x, [item.itemId]: 'pills' }))}>
                  <PillsIcon /> <span className="dock__label">Pills</span>
                </button>
                <button type="button" className={`dock__btn${!asPills ? ' dock__btn--on' : ''}`} aria-pressed={!asPills} onClick={() => setDisplay((x) => ({ ...x, [item.itemId]: 'list' }))}>
                  <ListIcon /> <span className="dock__label">List</span>
                </button>
              </>
            )}
            {textual && (
              <>
                <button type="button" className={`dock__btn${asCloud ? ' dock__btn--on' : ''}`} aria-pressed={asCloud} onClick={() => setDisplay((x) => ({ ...x, [item.itemId]: 'cloud' }))}>
                  <CloudIcon /> <span className="dock__label">Cloud</span>
                </button>
                <button type="button" className={`dock__btn${!asCloud ? ' dock__btn--on' : ''}`} aria-pressed={!asCloud} onClick={() => setDisplay((x) => ({ ...x, [item.itemId]: 'list' }))}>
                  <ListIcon /> <span className="dock__label">List</span>
                </button>
              </>
            )}
            {r.identified && r.myAccess && (
              <button type="button" className={`dock__btn${names ? ' dock__btn--on' : ''}`} aria-pressed={names} title="Show who said what" onClick={() => setNames((v) => !v)}>
                <PeopleIcon /> <span className="dock__label">Names</span>
              </button>
            )}
          </div>
        )}

        {owner && r.status === 'open' && (
          <div className="dock__group">
            <button
              type="button"
              className={`dock__btn dock__btn--live${live ? ' dock__btn--liveon' : ''}`}
              aria-pressed={live}
              title={live ? 'Phones show only the question on this screen' : 'Phones show only the question on this screen, and follow as you move'}
              onClick={toggleLive}
            >
              {live ? <span className="surveylive__dot" aria-hidden="true" /> : <BroadcastIcon />}
              {live ? 'Stop live' : 'Run live'}
            </button>
          </div>
        )}

        <div className="dock__group">
          <button type="button" className={`dock__btn dock__btn--icon${showQr ? ' dock__btn--on' : ''}`} aria-pressed={showQr} aria-label={showQr ? 'Hide the QR code' : 'Show the QR code'} title="QR code (Q)" onClick={() => setShowQr((v) => !v)}>
            <QrIcon size={20} />
          </button>
          <button type="button" className={`dock__btn dock__btn--icon${full ? ' dock__btn--on' : ''}`} aria-pressed={full} aria-label="Full screen" title="Full screen (F)" onClick={toggleFull}>
            <ExpandIcon size={20} />
          </button>
          <Link className="dock__btn dock__btn--icon" to={`/surveys/${id}/results`} aria-label="Leave the screen" title="Leave">
            <CloseIcon size={20} />
          </Link>
        </div>
      </nav>
    </div>
  )
}
